import { Provider } from "react-redux";
import merge from "lodash.merge";
// eslint-disable-next-line react/no-deprecated
import { render } from "react-dom";
import { initLogger } from "./logger";
import { sessionDataHandler } from "./sessionDataHandler";
import { getDispatch, getConfiguredStore } from "./store/store";
import { ConfigState, loadConfig } from "./store/features/configSlice";
import { setThemes } from "./store/features/themeSlice";
import { ZodFaq, setFaqs } from "./store/features/faqSlice";
import { applyWebchatFeatureConfig } from "./store/features/webchatFeatureConfigSlice";
import { SyncClient } from "twilio-sync";
import { z } from "zod";

import { configDataHandler } from "./configDataHandler";

const defaultConfig: ConfigState = {
  serverUrl: `${process.env.REACT_APP_BUILD_NOW_URL}`,
  fileAttachment: {
    enabled: true,
    maxFileSize: 16777216, // 16 MB
    acceptedExtensions: [
      "jpg",
      "jpeg",
      "png",
      "amr",
      "mp3",
      "mp4",
      "pdf",
      "txt",
    ],
  },
};

const store = getConfiguredStore();
const dispatch = getDispatch();

const initWebchat = async (config: ConfigState) => {
  let syncClient: SyncClient | null = null;

  const getSyncClient = async (): Promise<SyncClient> => {
    if (syncClient !== null) return syncClient;
    const token = await configDataHandler.getSyncToken();
    if (token === "") {
      throw Error("Failed to create new sync client");
    }
    syncClient = new SyncClient(token);
    return syncClient;
  };

  const getFaqItems = async () => {
    const client = await getSyncClient();
    const faqMap = await client.map("faq-map");
    let itemsPaginator = await faqMap.getItems();
    let items = itemsPaginator.items;
    while (itemsPaginator.hasNextPage) {
      itemsPaginator = await itemsPaginator.nextPage();
      items = [...items, ...itemsPaginator.items];
    }
    const zodItems = z.array(ZodFaq).parse(items);
    return { faqs: zodItems };
  };

  const mergedConfig = merge({}, defaultConfig, config);
  sessionDataHandler.setEndpoint(mergedConfig.serverUrl);
  dispatch(loadConfig(mergedConfig));

  const loadConfigData = await configDataHandler.getConfigData();
  const loadStyleConfig = await configDataHandler.getConfigStyle();
  const loadFaqItems = await getFaqItems();

  // ///////////////////////
  dispatch(
    setThemes({
      buildNowTheme: loadStyleConfig,
      webchatTheme: loadConfigData.theme,
    })
  );

  dispatch(applyWebchatFeatureConfig(loadConfigData));
  dispatch(setFaqs(loadFaqItems));

  initLogger();
  const rootElement = document.getElementById("twilio-webchat-widget-root");

  const { WebchatWidget } = await import("./components/WebchatWidget");

  render(
    <Provider store={store}>
      <WebchatWidget />
    </Provider>,
    rootElement
  );
};

declare global {
  interface Window {
    Twilio: {
      initWebchat: (config: ConfigState) => Promise<void>;
    };
  }
}

// Expose `initWebchat` function to window object
Object.assign(window, {
  Twilio: {
    initWebchat,
  },
});
